import React, { useEffect, useState } from 'react';
import {
  Wrench, Settings, Clock, RefreshCw, ShieldCheck, MessageCircle, LogOut,
  KeyRound, Loader2, Eye, EyeOff, X, Database, Sparkles, Smartphone,
} from 'lucide-react';
import {
  PengaturanMaintenance,
  DEFAULT_JUDUL,
  DEFAULT_PESAN,
  formatWaktuWIB,
  formatJamWIB,
  normalisasiNomorWa,
} from '../../utils/maintenance';

interface MaintenancePageProps {
  pengaturan: PengaturanMaintenance | null;
  onCobaLagi?: () => Promise<void> | void;
  /** Email akun yang sedang login tetapi bukan Super Admin. */
  emailMasuk?: string | null;
  onKeluar?: () => Promise<void> | void;
  /** Form login Super Admin; tidak ditampilkan saat pratinjau atau sudah login. */
  onLoginAdmin?: (email: string, password: string) => Promise<void>;
  /** Pratinjau di halaman pengaturan: tanpa tombol aksi, tinggi mengikuti wadah. */
  pratinjau?: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');

const useHitungMundur = (target: string | null) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  if (!target) return null;
  const sisa = new Date(target).getTime() - now;
  if (Number.isNaN(sisa)) return null;
  if (sisa <= 0) return { lewat: true, hari: 0, jam: 0, menit: 0, detik: 0 };
  const total = Math.floor(sisa / 1000);
  return {
    lewat: false,
    hari: Math.floor(total / 86400),
    jam: Math.floor((total % 86400) / 3600),
    menit: Math.floor((total % 3600) / 60),
    detik: total % 60,
  };
};

const pesanLoginGagal = (err: any): string => {
  const code = err?.code || '';
  if (code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')) {
    return 'Email atau password salah.';
  }
  if (code.includes('too-many-requests')) return 'Terlalu banyak percobaan. Coba lagi beberapa menit lagi.';
  if (code.includes('network')) return 'Koneksi internet bermasalah.';
  return 'Gagal masuk. Silakan coba lagi.';
};

export const MaintenancePage: React.FC<MaintenancePageProps> = ({
  pengaturan, onCobaLagi, emailMasuk, onKeluar, onLoginAdmin, pratinjau = false,
}) => {
  const judul = pengaturan?.judul?.trim() || DEFAULT_JUDUL;
  const pesan = pengaturan?.pesan?.trim() || DEFAULT_PESAN;
  const hitung = useHitungMundur(pengaturan?.selesai ?? null);
  const nomorWa = normalisasiNomorWa(pengaturan?.kontak_wa);

  const [memuat, setMemuat] = useState(false);
  const [dicekPada, setDicekPada] = useState<Date | null>(null);
  const [formTerbuka, setFormTerbuka] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [lihatPassword, setLihatPassword] = useState(false);
  const [sedangMasuk, setSedangMasuk] = useState(false);
  const [errorLogin, setErrorLogin] = useState<string | null>(null);

  const cobaLagi = async () => {
    if (!onCobaLagi || memuat) return;
    setMemuat(true);
    try {
      await onCobaLagi();
    } finally {
      setDicekPada(new Date());
      setMemuat(false);
    }
  };

  const masuk = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onLoginAdmin) return;
    setSedangMasuk(true);
    setErrorLogin(null);
    try {
      await onLoginAdmin(email.trim(), password);
    } catch (err) {
      setErrorLogin(pesanLoginGagal(err));
    } finally {
      setSedangMasuk(false);
    }
  };

  const linkWa = nomorWa
    ? `https://wa.me/${nomorWa}?text=${encodeURIComponent('Assalamualaikum, saya ingin bertanya tentang maintenance Portal MIAS.')}`
    : null;

  return (
    <div
      className={`relative w-full overflow-hidden bg-gradient-to-br from-[#0b2a2c] via-[#174143] to-[#0f5e57] text-white font-sans ${
        pratinjau ? 'min-h-[640px] rounded-2xl' : 'min-h-screen min-h-[100dvh]'
      }`}
    >
      {/* Ornamen latar */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-24 h-80 w-80 rounded-full bg-teal-400/20 blur-3xl" />
        <div className="absolute -bottom-40 -right-24 h-96 w-96 rounded-full bg-amber-400/10 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
      </div>

      <div className="relative mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-10 sm:px-6 sm:py-14 min-h-[inherit] justify-center">
        {/* Logo */}
        <div className="mb-8 flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lg ring-2 ring-white/30">
            <img src="/logo-transparan.png" alt="Logo MIAS" className="h-9 w-9 object-contain" />
          </span>
          <div className="leading-tight">
            <p className="text-sm font-bold tracking-wide">Portal MIAS</p>
            <p className="text-[11px] text-teal-100/70">Ma'had Ihya As-Sunnah Tasikmalaya</p>
          </div>
        </div>

        {/* Ilustrasi */}
        <div className="relative mb-7 h-32 w-32 sm:h-36 sm:w-36">
          <div className="absolute inset-0 rounded-full bg-white/10 ring-1 ring-white/15 backdrop-blur-sm" />
          <div className="absolute inset-3 rounded-full bg-gradient-to-br from-teal-400/30 to-teal-700/30 ring-1 ring-white/10" />
          <Settings
            className="absolute left-[18%] top-[18%] h-[46%] w-[46%] text-teal-100 animate-[spin_8s_linear_infinite]"
            strokeWidth={1.6}
          />
          <Settings
            className="absolute bottom-[16%] right-[16%] h-[30%] w-[30%] text-amber-300 animate-[spin_6s_linear_infinite_reverse]"
            strokeWidth={1.8}
          />
          <span className="absolute -right-1 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-amber-400 text-[#174143] shadow-lg">
            <Wrench className="h-5 w-5" />
          </span>
        </div>

        <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-amber-400/15 px-3 py-1 text-xs font-semibold text-amber-200 ring-1 ring-amber-300/30">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-300 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-300" />
          </span>
          Mode Maintenance
        </span>

        <h1 className="text-center text-2xl font-extrabold leading-tight sm:text-4xl">{judul}</h1>
        <p className="mt-3 max-w-xl whitespace-pre-line text-center text-sm leading-relaxed text-teal-50/85 sm:text-base">
          {pesan}
        </p>

        {/* Waktu */}
        <div className="mt-7 w-full rounded-2xl bg-white/10 p-4 ring-1 ring-white/15 backdrop-blur-md sm:p-5">
          {hitung && !hitung.lewat ? (
            <>
              <p className="mb-3 flex items-center justify-center gap-2 text-xs font-medium uppercase tracking-wider text-teal-100/80">
                <Clock className="h-4 w-4" /> Perkiraan selesai dalam
              </p>
              <div className="grid grid-cols-4 gap-2 sm:gap-3">
                {[
                  { n: hitung.hari, l: 'Hari' },
                  { n: hitung.jam, l: 'Jam' },
                  { n: hitung.menit, l: 'Menit' },
                  { n: hitung.detik, l: 'Detik' },
                ].map((x) => (
                  <div key={x.l} className="rounded-xl bg-[#0b2a2c]/50 py-2.5 text-center ring-1 ring-white/10">
                    <p className="text-2xl font-bold tabular-nums sm:text-3xl">{pad(x.n)}</p>
                    <p className="text-[10px] uppercase tracking-wider text-teal-100/70 sm:text-xs">{x.l}</p>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-center text-xs text-teal-100/75">
                Sekitar {formatWaktuWIB(pengaturan?.selesai ?? null)}
              </p>
            </>
          ) : hitung?.lewat ? (
            <div className="flex items-start gap-3 text-left">
              <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
              <div>
                <p className="text-sm font-semibold">Hampir selesai</p>
                <p className="text-xs leading-relaxed text-teal-50/80">
                  Pemeliharaan sedikit lebih lama dari perkiraan ({formatJamWIB(pengaturan?.selesai ?? null)}).
                  Halaman ini akan terbuka sendiri begitu selesai.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 text-left">
              <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
              <div>
                <p className="text-sm font-semibold">Sedang dikerjakan</p>
                <p className="text-xs leading-relaxed text-teal-50/80">
                  {pengaturan?.mulai ? `Dimulai ${formatWaktuWIB(pengaturan.mulai)}. ` : ''}
                  Halaman ini akan terbuka sendiri begitu selesai.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="mt-4 grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            { icon: <Database className="h-5 w-5" />, t: 'Data Anda aman', d: 'Tidak ada data yang hilang selama pemeliharaan.' },
            { icon: <Sparkles className="h-5 w-5" />, t: 'Terbuka otomatis', d: 'Tidak perlu memuat ulang, halaman berganti sendiri.' },
            { icon: <Smartphone className="h-5 w-5" />, t: 'Tetap terpasang', d: 'Aplikasi di HP tidak perlu dihapus atau dipasang ulang.' },
          ].map((x) => (
            <div key={x.t} className="flex items-start gap-3 rounded-xl bg-white/5 p-3 ring-1 ring-white/10 sm:flex-col sm:gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-400/20 text-teal-100">{x.icon}</span>
              <div>
                <p className="text-sm font-semibold">{x.t}</p>
                <p className="text-xs leading-relaxed text-teal-50/70">{x.d}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Aksi */}
        <div className="mt-6 flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={cobaLagi}
            disabled={pratinjau || memuat}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-[#174143] shadow-lg transition hover:bg-teal-50 disabled:opacity-70"
          >
            <RefreshCw className={`h-4 w-4 ${memuat ? 'animate-spin' : ''}`} />
            {memuat ? 'Memeriksa...' : 'Periksa Lagi'}
          </button>
          {linkWa && (
            <a
              href={pratinjau ? undefined : linkWa}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-emerald-600"
            >
              <MessageCircle className="h-4 w-4" /> Hubungi Admin
            </a>
          )}
        </div>
        {dicekPada && (
          <p className="mt-2 text-center text-[11px] text-teal-100/60">
            Masih dalam pemeliharaan. Diperiksa pukul{' '}
            {dicekPada.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}.
          </p>
        )}

        {/* Akun */}
        {!pratinjau && emailMasuk && (
          <div className="mt-6 flex w-full flex-col items-center gap-2 rounded-xl bg-black/15 p-3 text-center ring-1 ring-white/10 sm:flex-row sm:justify-between sm:text-left">
            <p className="text-xs text-teal-50/80">
              Masuk sebagai <span className="font-semibold text-white">{emailMasuk}</span>. Selama maintenance hanya
              Super Admin yang bisa membuka aplikasi.
            </p>
            {onKeluar && (
              <button
                type="button"
                onClick={() => onKeluar()}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-teal-50 ring-1 ring-white/25 hover:bg-white/10"
              >
                <LogOut className="h-3.5 w-3.5" /> Keluar
              </button>
            )}
          </div>
        )}

        {!pratinjau && !emailMasuk && onLoginAdmin && (
          <div className="mt-8 w-full max-w-sm">
            {!formTerbuka ? (
              <button
                type="button"
                onClick={() => setFormTerbuka(true)}
                className="mx-auto flex items-center gap-1.5 text-xs font-medium text-teal-100/60 hover:text-white"
              >
                <KeyRound className="h-3.5 w-3.5" /> Masuk sebagai Super Admin
              </button>
            ) : (
              <form onSubmit={masuk} className="rounded-2xl bg-white p-4 text-gray-800 shadow-2xl">
                <div className="mb-3 flex items-center justify-between">
                  <p className="flex items-center gap-2 text-sm font-bold text-[#174143]">
                    <ShieldCheck className="h-4 w-4" /> Login Super Admin
                  </p>
                  <button
                    type="button"
                    onClick={() => { setFormTerbuka(false); setErrorLogin(null); }}
                    className="rounded-full p-1 text-gray-400 hover:bg-gray-100"
                    aria-label="Tutup"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mb-2 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                />
                <div className="relative mb-2">
                  <input
                    type={lihatPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 pr-10 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                  />
                  <button
                    type="button"
                    onClick={() => setLihatPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400"
                    aria-label={lihatPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                  >
                    {lihatPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errorLogin && <p className="mb-2 text-xs font-medium text-red-600">{errorLogin}</p>}
                <button
                  type="submit"
                  disabled={sedangMasuk}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-primary py-2.5 text-sm font-bold text-white hover:bg-brand-secondary disabled:opacity-70"
                >
                  {sedangMasuk && <Loader2 className="h-4 w-4 animate-spin" />} Masuk
                </button>
                <p className="mt-2 text-center text-[11px] text-gray-500">Akun selain Super Admin tetap melihat halaman ini.</p>
              </form>
            )}
          </div>
        )}

        <p className="mt-8 text-center text-[11px] text-teal-100/50">
          © {new Date().getFullYear()} Portal MIAS · Terima kasih atas kesabaran Anda
        </p>
      </div>
    </div>
  );
};

export default MaintenancePage;
